import type { Deployment, PlanResult, VerificationResult } from '../types';

/**
 * Unified event type for history display
 */
export interface HistoryEvent {
  type: 'deployment' | 'plan' | 'verification';
  timestamp: string;
  environment: string;
  version?: string;
  status?: string;
  hasChanges?: boolean;
  createdBy: string;
  details: Deployment | PlanResult | VerificationResult;
}

/**
 * Aggregate and sort all events for a specific app
 */
export const getAppHistory = (
  appName: string,
  deployments: Deployment[],
  planResults: PlanResult[],
  verificationResults: VerificationResult[],
): HistoryEvent[] => {
  const events: HistoryEvent[] = [];

  // Add deployment events
  deployments
    .filter((d) => d.appName === appName)
    .forEach((deployment) => {
      events.push({
        type: 'deployment',
        timestamp: deployment.createdAt,
        environment: deployment.environment,
        version: deployment.version,
        status: deployment.status,
        createdBy: deployment.createdBy,
        details: deployment,
      });
    });

  // Add plan result events
  planResults
    .filter((p) => p.appName === appName)
    .forEach((plan) => {
      events.push({
        type: 'plan',
        timestamp: plan.createdAt,
        environment: plan.environment,
        version: plan.version,
        hasChanges: plan.hasChanges,
        createdBy: plan.createdBy,
        details: plan,
      });
    });

  // Add verification events
  // We need to match verifications to deployments to get environment and version
  const deploymentsById = new Map(deployments.map((d) => [d.id, d]));
  verificationResults.forEach((verification) => {
    const deployment = deploymentsById.get(verification.deploymentId);
    if (deployment && deployment.appName === appName) {
      events.push({
        type: 'verification',
        timestamp: verification.createdAt,
        environment: deployment.environment,
        version: deployment.version,
        status: verification.status,
        createdBy: verification.createdBy,
        details: verification,
      });
    }
  });

  // Sort by timestamp (newest first)
  return events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
};

/**
 * Format timestamp for display
 */
export const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);

  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

/**
 * Format timestamp relative to now for compact recent-event display
 */
export const formatRelativeTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const absSeconds = Math.abs(diffSeconds);

  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['week', 60 * 60 * 24 * 7],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
    ['second', 1],
  ];

  const [unit, secondsInUnit] =
    units.find(([, secondsInUnit]) => absSeconds >= secondsInUnit) ?? units[units.length - 1];

  const value = Math.round(diffSeconds / secondsInUnit);
  return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(value, unit);
};
